import { Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  ViewChild,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { GoogleMapService } from 'src/app/modules/shared/services/google.map.service';

@Component({
  selector: 'ig-address-input',
  templateUrl: './address.input.component.html',
  styleUrls: ['./address.input.component.scss'],
})
export class AddressInputComponent implements AfterViewInit, OnDestroy {
  googleLoaded$: Observable<boolean>;
  placeAutocompleteElement: google.maps.places.PlaceAutocompleteElement;
  placeListener: google.maps.MapsEventListener;
  isUsingNewElement = false;

  @Input() label: string = 'Location / Address* (or check Virtual Events)';
  @Input() hint: string =
    'Start typing the physical address and select from a verified Google Maps address below';
  @Input() control: FormControl;

  @ViewChild('autocompleteContainer', { static: true })
  autocompleteContainer!: ElementRef<HTMLDivElement>;

  @ViewChild('inputElement', { static: true })
  inputElement!: any;

  constructor(
    public googleMapService: GoogleMapService,
    private ngZone: NgZone
  ) {
    try {
      this.initLabelOfInput();
      this.googleLoaded$ = this.googleMapService.loadGoogleMapApi();
      applyGmpBorderPatchOnce();
    } catch (e) {
      console.error(e);
    }
  }

  ngAfterViewInit() {
    try {
      this.googleLoaded$.subscribe((isLoaded: boolean) => {
        if (!isLoaded) return;

        // Check if we can use the new PlaceAutocompleteElement
        this.initializeAutocomplete();
      });
    } catch (e) {
      console.error(e);
    }
  }

  ngOnDestroy() {
    if (this.placeListener) {
      google.maps.event.removeListener(this.placeListener);
    }

    if (
      this.placeAutocompleteElement &&
      this.placeAutocompleteElement.parentNode
    ) {
      this.placeAutocompleteElement.parentNode.removeChild(
        this.placeAutocompleteElement
      );
    }
  }

  async initializeAutocomplete() {
    try {
      // Try to use the new PlaceAutocompleteElement
      const placesLibrary = this.googleMapService.getPlacesLibrary();

      if (placesLibrary && google.maps.places.PlaceAutocompleteElement) {
        this.initializeNewAutocomplete();
      } else {
        // Fallback to old method if new element is not available
        this.initializeLegacyAutocomplete();
      }
    } catch (error) {
      console.error('Error initializing autocomplete:', error);
      // Fallback to legacy method
      this.initializeLegacyAutocomplete();
    }
  }

  initializeNewAutocomplete() {
    const el = document.createElement('gmp-place-autocomplete') as any;

    el.setAttribute('fields', 'formattedAddress,location,addressComponents,id');
    this.autocompleteContainer.nativeElement.appendChild(el);
    this.isUsingNewElement = true;

    const placeholderEl = document.createElement('div');
    placeholderEl.textContent = this.label;
    placeholderEl.style.position = 'absolute';
    placeholderEl.style.top = '20px';
    placeholderEl.style.left = '16px';
    placeholderEl.style.color = '#9e9e9e';
    placeholderEl.style.fontSize = '16px';
    placeholderEl.style.fontFamily = 'inherit';
    placeholderEl.style.pointerEvents = 'none';

    this.autocompleteContainer.nativeElement.appendChild(placeholderEl);

    Object.assign(el.style, {
      width: '100%',
      padding: '12px 16px',
      marginBottom: '20px',
      border: '1px solid #b3b3b3',
      borderRadius: '4px',
      fontSize: '16px',
      fontFamily: 'inherit',
      outline: 'none',
      boxSizing: 'border-box',
    });

    // Focus/blur styling
    el.addEventListener('focus', () => {
      placeholderEl.style.display = 'none';
      el.style.border = '2px solid #673ab7';
    });
    el.addEventListener('blur', () => {
      if (!el.value || !el.value.trim() || el.value === '') {
        placeholderEl.style.display = 'block';
        placeholderEl.style.color = 'red';
        el.style.border = '2px solid red';
      } else {
        //placeholderEl.style.display = 'block';
        el.style.border = '1px solid #b3b3b3'; // or your normal color
      }
    });

    el.addEventListener('gmp-select', async ({ placePrediction }: any) => {
      try {
        /*turn the prediction into a Place */
        const place = await placePrediction.toPlace();

        /*ask the server for the fields you want */
        await place.fetchFields({
          fields: ['formattedAddress', 'location', 'addressComponents', 'id'],
        });

        /* 3️⃣ hand it off to your handler */
        this.ngZone.run(() => this.handlePlaceSelection(place));
      } catch (err) {
        console.error('Failed to load place details', err);
      }
    });

    this.placeAutocompleteElement =
      el as google.maps.places.PlaceAutocompleteElement;
  }

  initializeLegacyAutocomplete() {
    // Fallback to the old Autocomplete method
    const input = this.inputElement.input.nativeElement as HTMLInputElement;

    const autoComplete = new google.maps.places.Autocomplete(input, {
      fields: [
        'address_components',
        'geometry',
        'formatted_address',
        'name',
        'place_id',
      ],
    });

    // Prevent zoom-in on iOS when input is focused
    input.style.fontSize = '16px';

    // iOS specific fixes
    this.applyIOSFixes(input);

    // Add place_changed event listener
    this.placeListener = autoComplete.addListener('place_changed', () => {
      this.ngZone.run(() => this.handlePlaceSelection(autoComplete.getPlace()));
    });

    console.log('Using legacy Autocomplete');
  }

  applyIOSFixes(input: HTMLInputElement) {
    // Always make the field editable
    const unlock = () => input.removeAttribute('readonly');

    // Google adds readonly on 'focus', so undo it right away
    input.addEventListener('focus', unlock, true);
    // Some Android devices only fire touchstart/end before focus
    input.addEventListener('touchstart', unlock, true);

    // Monitor DOM for pac-container and apply fixes when it appears
    this.addPacContainerFix();
  }

  addPacContainerFix() {
    // Create a mutation observer to detect when Google adds the pac-container to the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          // Look for pac-container in the document
          const pacContainer = document.querySelector('.pac-container');
          if (pacContainer) {
            // Add z-index to ensure it's visible
            (pacContainer as HTMLElement).style.zIndex = '1000';

            // Prevent touchend events from bubbling up
            pacContainer.addEventListener(
              'touchend',
              (e) => {
                e.stopImmediatePropagation();
              },
              true
            );

            // Apply needsclick to all pac-items to prevent fastclick issues
            const pacItems = pacContainer.querySelectorAll(
              '.pac-item, .pac-item span'
            );
            pacItems.forEach((item) => {
              item.classList.add('needsclick');
            });

            // Once we've found and fixed the pac-container, disconnect the observer
            observer.disconnect();
          }
        }
      });
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  handlePlaceSelection(place: any) {
    if (!place?.formattedAddress) {
      console.warn('No place details', place);
      return;
    }

    const comps = place.addressComponents ?? [];
    const get = (t: string, short = false) =>
      comps.find((c) => c.types.includes(t))?.[
        short ? 'shortText' : 'longText'
      ] ?? '';

    const placeData = {
      lat: place.location?.lat() ?? null,
      lng: place.location?.lng() ?? null,
      address: place.formattedAddress,
      address_components: comps,
      placeId: place.id,
      cityName: get('locality'),
      stateAbbr: get('administrative_area_level_1', true),
      country: get('country'),
    };

    this.control.setValue(placeData);
    this.control.markAsDirty();
    this.control.markAsTouched();

    /* keep the UI in sync */
    (this.placeAutocompleteElement as any).value = place.formattedAddress;
  }

  clear() {
    this.control.setValue(null);

    if (this.isUsingNewElement) {
      (this.placeAutocompleteElement as any).value = '';
    } else {
      this.inputElement.input.nativeElement.value = '';
    }
  }

  initLabelOfInput() {
    const currentUrl = window.location.href;

    if (currentUrl.includes('/events')) {
      this.label = 'Location / Address* (or check Virtual Events)';
    } else {
      this.label = 'Location / Address*';
    }
  }
}
function applyGmpBorderPatchOnce(): void {
  // Guard so we don't patch more than once
  if ((window as any).__gmpBorderPatchApplied) {
    return;
  }
  (window as any).__gmpBorderPatchApplied = true;

  const originalAttach = Element.prototype.attachShadow;

  Element.prototype.attachShadow = function (init: ShadowRootInit) {
    // Intercept only <gmp-place-autocomplete>
    if (this.localName === 'gmp-place-autocomplete') {
      // force mode:"open" so we can style inside
      const shadow = originalAttach.call(this, { ...init, mode: 'open' });

      // Inject <style> that kills the inner border
      const style = document.createElement('style');
      style.textContent = `
        /* hides the nested border that causes the “double border” */

       .widget-container,
          .widget-container:focus-within,
          .input-container,
          .input-container:focus-within {
            border: none !important;
          }

          /* suppress native blue focus ring on the input itself */
          input {
            outline: none !important;
            box-shadow: none !important;
            border: none !important;
          }

          .focus-ring {
            outline: none !important;
            box-shadow: none !important;
            border: none !important;
          }

          .input-container {
            padding: 8px 0 !important;
            font-size: 18px !important;
          }


      `;
      shadow.appendChild(style);

      return shadow; // expose the patched shadow root
    }

    // All other elements keep their default behaviour
    return originalAttach.call(this, init);
  };
}
